/**
 * client/camera/fov.ts — field of view (pure).
 *
 * OWNER: VIEWMODEL+CAMERA agent.
 * - cg_fov is the HORIZONTAL fov of a 4:3 screen; the engine keeps tanHalfFovY = tan(fov/2)*0.75 for every
 *   aspect (Hor+) [T5 cgame/cg_camera.cpp CG_CalcFov; MV §1.6; DVAR cg_fov 65]. 65 -> 51.08 deg vertical.
 * - ADS zoom [T5 cgame/cg_camera.cpp CG_GetViewFov]: the lerp happens in cg_fov (horizontal-4:3) degrees,
 *   weaponFov = min(adsZoomFov, cg_fov); zoom only starts once posLerp passes (1 - adsZoomInFrac) going in
 *   (or 1 - adsZoomOutFrac going out); bPositionToADS is latched when posLerp hits 0 (in) / 1 (out)
 *   [T5 cgame/cg_weapons.cpp CalculateWeaponPostion_PositionToADS]. Result * cg_fovScale, clamped >= cg_fovMin.
 */
import { CG_FOV_MIN, CG_FOV_SCALE } from './dvars';

/** cg_fov (horizontal at 4:3) -> vertical FOV in degrees. */
export function cgFovToVertical(hfov4x3: number): number {
  return (2 * Math.atan(Math.tan((hfov4x3 * Math.PI) / 360) * 0.75) * 180) / Math.PI;
}

/** Horizontal FOV for a given aspect from a cg_fov value (for display / tests). */
export function cgFovToHorizontal(hfov4x3: number, aspect: number): number {
  return (2 * Math.atan(Math.tan((hfov4x3 * Math.PI) / 360) * 0.75 * aspect) * 180) / Math.PI;
}

export interface AdsFovDef {
  aimDownSight: boolean;
  adsZoomFov: number;
  adsZoomInFrac: number;
  adsZoomOutFrac: number;
}

/** CalculateWeaponPostion_PositionToADS: latch the zoom direction at the ends of the ADS lerp. */
export function updatePositionToAds(prev: boolean, adsFrac: number): boolean {
  if (adsFrac === 1) return false;
  if (adsFrac === 0) return true;
  return prev;
}

/**
 * CG_GetViewFov: view fov in cg_fov units (horizontal at 4:3).
 * @param cgFov player's cg_fov (settings)
 * @param positionToAds true while zooming in (latched, see updatePositionToAds)
 */
export function viewFov(cgFov: number, def: AdsFovDef | null, adsFrac: number, positionToAds: boolean): number {
  let fov = cgFov;
  if (def && def.aimDownSight && def.adsZoomFov > 0.01) {
    const weaponFov = def.adsZoomFov - cgFov < 0 ? def.adsZoomFov : cgFov;
    if (adsFrac === 1) fov = weaponFov;
    else if (adsFrac !== 0) {
      const frac = positionToAds ? def.adsZoomInFrac : def.adsZoomOutFrac;
      let zoomFrac = adsFrac - (1 - frac);
      if (zoomFrac > 0) zoomFrac = frac > 0 ? zoomFrac / frac : 1;
      if (zoomFrac > 0) fov = fov - (fov - weaponFov) * zoomFrac;
    }
  }
  fov *= CG_FOV_SCALE;
  return fov > CG_FOV_MIN ? fov : CG_FOV_MIN;
}
